import DTO from './DTO';
import { RULES_TYPE_LIST, VEHICLE_STATE_LIST } from '../configs';
import { required } from '../utils/validation';

export const RULE_MESSAGE_LOCALE = 'ru';
export const RULE_NAME_REQUIRED_MESSAGE = 'Введите название';
export const RULE_TYPE_REQUIRED_MESSAGE = 'Выберите тип правила';
export const RULE_BOUNDS_MESSAGE = 'Максимальное значение не может быть меньше минимального';

export const validateRuleName = value => {
  if (typeof value !== 'string' || !value.trim()) {
    return RULE_NAME_REQUIRED_MESSAGE;
  }
  return {};
};

export const validateRuleType = value => (
  RULES_TYPE_LIST.some(item => item.value === value) ? {} : RULE_TYPE_REQUIRED_MESSAGE
);

export const validateRuleBounds = (minimum, maximum) => (
  minimum !== null && maximum !== null && maximum < minimum ? RULE_BOUNDS_MESSAGE : {}
);

export const normalizeRuleMessageForForm = value => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'null' ? '' : value;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const localized = value[RULE_MESSAGE_LOCALE];
    if (localized === null || typeof localized === 'undefined') {
      const fallback = Object.values(value).find(item => typeof item === 'string' && item.trim());
      return fallback || '';
    }
    return typeof localized === 'string' ? localized : '';
  }
  return '';
};

export const normalizeRuleMessageForApi = value => {
  const text = normalizeRuleMessageForForm(value).trim();
  return text || null;
};

export const getRuleStateFromStates = states => {
  if (!states || typeof states !== 'object' || Array.isArray(states)) {
    return '';
  }

  const [state] = Object.keys(states);
  return state || '';
};

export const getStatesFromRuleState = state => {
  if (!state) {
    return null;
  }
  return {
    [state]: null,
  };
};

const isValidRuleState = state => !state || VEHICLE_STATE_LIST.some(item => item.value === state);

export const normalizeRuleStates = states => {
  if (!states) {
    return null;
  }

  if (typeof states === 'string') {
    if (!isValidRuleState(states)) {
      throw new Error('Выберите корректное состояние ТС');
    }
    return getStatesFromRuleState(states);
  }

  if (typeof states === 'object' && !Array.isArray(states)) {
    const state = getRuleStateFromStates(states);
    if (!isValidRuleState(state)) {
      throw new Error('Выберите корректное состояние ТС');
    }
    return state ? { [state]: states[state] ?? null } : null;
  }

  throw new Error('Состояние ТС должно быть выбрано из списка');
};

class RulesDTO extends DTO {
  name;

  rule_id;

  rule_type;

  geographies;

  states;

  rule_units;

  vehicle_types;

  propulsion_types;

  minimum;

  maximum;

  inclusive_minimum;

  inclusive_maximum;

  rate_amount;

  rate_recurrence;

  rate_applies_when;

  start_time;

  end_time;

  days;

  message;

  value_url;

  created_at;

  constructor(params, isValidInit) {
    const normalizedParams = params ? { ...params } : params;
    if (normalizedParams) {
      normalizedParams.message = normalizeRuleMessageForForm(normalizedParams.message);
    }

    const attributes = [
      {
        name: 'name',
        type: String,
        validators: [validateRuleName],
        labelKey: 'rules.name',
      },
      {
        name: 'rule_id',
        type: String,
        validators: [],
        labelKey: 'rules.rule_id',
      },
      {
        name: 'rule_type',
        type: String,
        validators: [validateRuleType],
        labelKey: 'rules.rule_type',
      },
      {
        name: 'geographies',
        type: Array,
        validators: [required],
        labelKey: 'rules.geographies',
      },
      {
        name: 'states',
        validators: [required],
        labelKey: 'rules.states',
      },
      {
        name: 'rule_units',
        type: String,
        validators: [],
        labelKey: 'rules.rule_units',
      },
      {
        name: 'vehicle_types',
        type: Array,
        validators: [],
        labelKey: 'rules.vehicle_types',
      },
      {
        name: 'propulsion_types',
        type: Array,
        validators: [],
        labelKey: 'rules.propulsion_types',
      },
      {
        name: 'minimum',
        type: String,
        validators: [],
        labelKey: 'rules.minimum',
      },
      {
        name: 'maximum',
        type: String,
        validators: [],
        labelKey: 'rules.maximum',
      },
      {
        name: 'inclusive_minimum',
        type: String,
        validators: [],
        labelKey: 'rules.inclusive_minimum',
      },
      {
        name: 'inclusive_maximum',
        type: String,
        validators: [],
        labelKey: 'rules.inclusive_maximum',
      },
      {
        name: 'rate_amount',
        type: String,
        validators: [],
        labelKey: 'rules.rate_amount',
      },
      {
        name: 'rate_recurrence',
        type: String,
        validators: [],
        labelKey: 'rules.rate_recurrence',
      },
      {
        name: 'rate_applies_when',
        type: String,
        validators: [],
        labelKey: 'rules.rate_applies_when',
      },
      {
        name: 'start_time',
        type: String,
        validators: [],
        labelKey: 'rules.start_time',
      },
      {
        name: 'end_time',
        type: String,
        validators: [],
        labelKey: 'rules.end_time',
      },
      {
        name: 'days',
        type: Array,
        validators: [],
        labelKey: 'rules.days',
      },
      {
        name: 'message',
        type: String,
        validators: [],
        labelKey: 'rules.messages',
      },
      {
        name: 'value_url',
        type: String,
        validators: [],
        labelKey: 'rules.value_url',
      },
      {
        name: 'created_at',
        type: String,
        validators: [],
        labelKey: 'created_at',
      },
    ];
    super(attributes, normalizedParams, isValidInit);
  }

  getData() {
    const data = {
      name: typeof this.name === 'string' ? this.name.trim() : '',
      rule_type: this.rule_type,
      geographies: this.geographies,
      states: normalizeRuleStates(this.states),
      message: normalizeRuleMessageForApi(this.message),
    };
    if (this?.rule_id?.length) { data.rule_id = this.rule_id; }
    if (this?.start_time?.length) { data.start_time = this.start_time; }
    if (this?.end_time?.length) { data.end_time = this.end_time; }
    if (this?.inclusive_maximum?.length) { data.inclusive_maximum = this.inclusive_maximum; }
    if (this?.inclusive_minimum?.length) { data.inclusive_minimum = this.inclusive_minimum; }
    if (this?.maximum?.length) { data.maximum = this.maximum; }
    if (this?.minimum?.length) { data.minimum = this.minimum; }
    if (this?.propulsion_types?.length) { data.propulsion_types = this.propulsion_types; }
    if (this?.rate_amount?.length) { data.rate_amount = this.rate_amount; }
    if (this?.rate_applies_when?.length) { data.rate_applies_when = this.rate_applies_when; }
    if (this?.rate_recurrence?.length) { data.rate_recurrence = this.rate_recurrence; }
    if (this?.rule_units?.length) { data.rule_units = this.rule_units; }
    if (this?.schedule?.length) { data.schedule = this.schedule; }
    if (this?.value_url?.length) { data.value_url = this.value_url; }
    if (this?.vehicle_types?.length) { data.vehicle_types = this.vehicle_types; }
    if (this?.days?.length) { data.days = this.days; }

    return data;
  }
}

export default RulesDTO;
